import Modal from "@mui/material/Modal";
import {Button, Grid, InputAdornment, Stack, TextField, Typography} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import * as yup from "yup";
import {useFormik} from "formik";
import {IconChevronRight, IconMail, IconPhoneCall} from "@tabler/icons-react";
import {doc, getDoc, setDoc} from "firebase/firestore";
import {toast} from "react-toastify";
import {v4 as uuid} from "uuid";
import {useAtom} from "jotai";
import {RequestLicenseModalAtom} from "../../atoms";
import {AppContext} from "../../context/AppContext";
import {LicenseRequestType, LicenseType} from "../../utils/enums/LicenseRequestType";
import {auth, db} from "../../config/firebase";

const style = {
  position: 'absolute',
  top: '50%', left: '50%',
  transform: 'translate(-47%, -50%)',
  maxWidth: {xs: "90vw", md: "60vw", lg: "40vw"}, maxHeight: "95dvh", minWidth: "300px",
  minHeight: '80 dvh',
  bgcolor: 'background.paper',
  borderRadius: 8, border: 0,
  boxShadow: 24, p: 3, pr: 5, pb: 5, overflowY: "auto"
};

const validationSchema = yup.object({
  firstName: yup
    .string('Enter your name')
    .required('Name is required'),
  lastName: yup
    .string('Enter your last name')
    .required('Last name is required'),
  email: yup
    .string('Enter your email')
    .email('Enter a valid email')
    .required('Email is required'),
  phoneNumber: yup
    .string('Enter your phone number')
    .notRequired(),
  details: yup
    .string('Enter details')
    .notRequired(),
});

export const RequestLicenseModal = () => {
  const [loading, setLoading] = useState(false);
  const [license, setLicense] = useAtom(RequestLicenseModalAtom);
  const [licenseUser, setLicenseUser] = useState(null)
  const open = Boolean(license);

  const {state: {userDetails}} = useContext(AppContext);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      details: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      if (license?.userUid === auth.currentUser?.uid) {
        toast.info("If someone other than yourself submitted interest, you’ll receive a notification.")
        return;
      }
      try {
        if (!license || !license?.uid) {
          return;
        }

        const uid = uuid();

        const createdAt = new Date().getTime();
        
        await setDoc(
          doc(db, "license_requests", uid),
          {
            uid,
            ...values,
            recipientMail: licenseUser?.email,
            state: LicenseRequestType.PENDING,
            type: LicenseType.REQUEST_LICENCE,
            licenseUid: license?.uid,
            nftUid: license?.nftUid,
            createdAt: createdAt,
            expirationDate: new Date(createdAt + (7 * 24 * 60 * 60 * 1000)).getTime(),
          }
        )
        toast.success(`License request sent'`);

        formik.resetForm({
          firstName: userDetails?.firstName || '',
          lastName: userDetails?.lastName || '',
          email: userDetails?.email || '',
          phoneNumber: userDetails?.phoneNumber || '',
          details: ""
        })
        handleClose();
      } catch (e) {
        console.log(e);
        toast.error("Failed to sign template")
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (userDetails?.uid) {
      formik.setValues({
        firstName: userDetails?.firstName || '',
        lastName: userDetails?.lastName || '',
        email: userDetails?.email || '',
        phoneNumber: userDetails?.phoneNumber || '',
        details: ""
      })
    }
  }, [userDetails]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!license?.userUid) {
        return;
      }
      const docRef = doc(db, "users", license?.userUid);
      const docSnap = await getDoc(docRef);

      if (docSnap?.exists()) {
        const details = {...(docSnap?.data() || {})}
        setLicenseUser(details)
      }
    }
    if (open) {
      fetchUser();
    }
  }, [open, license])

  const handleClose = () => setLicense(null)

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      onClose={handleClose}
    >
      <Grid container component={"form"} onSubmit={formik.handleSubmit} spacing={2} sx={style}>
        <Grid item xs={12}>
          <Stack direction={"column"} gap={1} maxWidth={"sm"} sx={{textAlign: 'center', mx: "auto"}}>
            <Typography variant={"h3"} fontWeight={600}>Request license</Typography>
            <Typography variant={"caption"} color="GrayText" sx={{pb: 1, pt: 0, mt: 2}} fontWeight={400}>
              We will send a
              notification to the creator to send you this license.
            </Typography>
            <Typography variant="caption" color="GrayText">
              What is your contact information?
              Only your Username will be shared with the licensee.
              We contact the licensee via C3’s email address.
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            sx={{flex: 1}} fullWidth
            InputLabelProps={{shrink: true, sx: {color: "#000", fontWeight: 700}}}
            InputProps={{sx: {borderRadius: 3, bgcolor: "#FFF"}}}
            label={"First name *"}
            placeholder={"Name"}
            id="firstName"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            sx={{flex: 1}} fullWidth
            InputLabelProps={{shrink: true, sx: {color: "#000", fontWeight: 700}}}
            InputProps={{sx: {borderRadius: 3, bgcolor: "#FFF"}}}
            label={"Last name *"}
            placeholder={"Last name"}
            id="lastName"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            sx={{flex: 1}} fullWidth
            InputLabelProps={{shrink: true, sx: {color: "#000", fontWeight: 700}}}
            InputProps={{
              sx: {borderRadius: 3, bgcolor: "#FFF"},
              endAdornment: <InputAdornment position={"end"}><IconMail/></InputAdornment>
            }}
            label={"Email *"}
            placeholder={"yourmail@gmail.com"}
            id="email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            sx={{flex: 1}} fullWidth
            InputLabelProps={{shrink: true, sx: {color: "#000", fontWeight: 700}}}
            InputProps={{
              sx: {borderRadius: 3, bgcolor: "#FFF"},
              endAdornment: <InputAdornment position={"end"}><IconPhoneCall/></InputAdornment>
            }}
            label={"Phone *"}
            placeholder={"Enter your phone number"}
            id="phoneNumber"
            name="phoneNumber"
            value={formik.values.phoneNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
            helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
          />
        </Grid>
        <Grid item xs={12} textAlign={'center'}>
          <Stack direction={"column"} alignItems={"center"} gap={1}>
            <TextField
              fullWidth
              InputLabelProps={{shrink: true, sx: {color: "#000", fontWeight: 700}}}
              InputProps={{sx: {borderRadius: 3, bgcolor: "#FFF"}}}
              label={"Details *"}
              placeholder={"Enter details for license"}
              multiline minRows={3}
              id="details"
              name="details"
              value={formik.values.details}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.details && Boolean(formik.errors.details)}
              helperText={formik.touched.details && formik.errors.details}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Button
            type={"submit"} disabled={loading || !formik.isValid || !licenseUser}
            endIcon={<IconChevronRight/>} className={"sign-button"}
            sx={{borderRadius: 3, py: 1}} fullWidth
          >
            Request License
          </Button>
        </Grid>
      </Grid>
    </Modal>
  )
}